/**
 * Tracks which of a transcript's artifact cards have scrolled past the top of the scroll viewport, and
 * returns the one whose compact copy should be pinned there (rules in `chat/lib/sticky-artifact.ts`).
 *
 * WHY AN OBSERVER AND NOT `position: sticky`. There is no `:stuck` selector to key the compact variant off,
 * and a sticky card stays in flow — so collapsing it while pinned would pull the transcript up by the height
 * it lost, mid-scroll. Sticky also cannot outlive its own parent box. An overlay driven by this hook costs
 * no layout and leaves the message list flat.
 *
 * `observeEpoch` is REQUIRED, not defaulted: it must change when the observed NODE moves even if the identity
 * keys do not (two cards for one dashboard share a key). A keys-only fallback cannot see that, so the effect
 * would keep watching the first card — exactly the bug the epoch exists to prevent.
 */
import { useEffect, useState } from 'react';

import { CHAT_ARTIFACT_KEY_ATTRIBUTE } from '../lib/chat-artifacts';
import { DEFAULT_REVEAL_RATIO, isScrolledPast, pickStickyKey } from '../lib/sticky-artifact';

const EMPTY: ReadonlySet<string> = new Set();

/** True when both sets hold exactly the same keys — so a re-seed can skip minting a new Set reference. */
function sameKeys(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const key of a) if (!b.has(key)) return false;
  return true;
}

export function useStickyArtifactKey(
  containerRef: React.RefObject<HTMLElement | null>,
  /** Card keys in the physical order of the observed cards (topmost first). */
  orderedKeys: string[],
  /** Token that changes whenever the observed node for any key moves. */
  observeEpoch: string,
): string | null {
  const [scrolledPast, setScrolledPast] = useState<ReadonlySet<string>>(EMPTY);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || observeEpoch.length === 0) {
      setScrolledPast((prev) => (prev.size === 0 ? prev : EMPTY));
      return;
    }

    const nodes = container.querySelectorAll(`[${CHAT_ARTIFACT_KEY_ATTRIBUTE}]`);

    // Re-seed from current geometry instead of blanket-clearing: clearing would drop the pin of a card that
    // is still genuinely scrolled past until the async observer re-fires.
    const rootTop = container.getBoundingClientRect().top;
    const seeded = new Set<string>();
    for (const node of nodes) {
      const key = node.getAttribute(CHAT_ARTIFACT_KEY_ATTRIBUTE);
      if (key != null && isScrolledPast(node.getBoundingClientRect(), rootTop)) seeded.add(key);
    }
    // Keep the previous reference when membership is unchanged, or every epoch bump (each new artifact,
    // mid-stream) commits a fresh Set and re-renders the whole transcript for nothing.
    setScrolledPast((prev) => (sameKeys(prev, seeded) ? prev : seeded));

    const observer = new IntersectionObserver(
      (entries) => {
        // `rootBounds` is populated for a same-document root; measure once as a fallback rather than
        // forcing a layout per entry inside a scroll-driven callback.
        const callbackRootTop = entries[0]?.rootBounds?.top ?? container.getBoundingClientRect().top;
        setScrolledPast((prev) => {
          const next = new Set(prev);
          let changed = false;
          for (const entry of entries) {
            const key = entry.target.getAttribute(CHAT_ARTIFACT_KEY_ATTRIBUTE);
            if (key == null) continue;
            const past = isScrolledPast(entry.boundingClientRect, entry.rootBounds?.top ?? callbackRootTop);
            if (past === next.has(key)) continue;
            if (past) next.add(key);
            else next.delete(key);
            changed = true;
          }
          // A scroll that changes nothing must not mint a new Set, or every tick re-renders the transcript.
          return changed ? next : prev;
        });
      },
      // Fire on entry/exit AND at the handover point, so the pin flips mid-scroll rather than only once
      // the card has left entirely.
      //
      // ASSUMES A CARD SHORTER THAN THE SCROLL VIEWPORT. Intersection ratio is card-relative, so a card
      // taller than the viewport never reaches ratio 1 and one taller than ~2x never reaches 0.5 — the
      // callback would then only fire at full entry/exit and the 50% handover would go unevaluated.
      // Artifact cards are one or two lines, so this holds today. Finer thresholds would NOT fix it (the
      // ratio plateaus inside a very tall target); a resize-driven re-measure would.
      { root: container, threshold: [0, DEFAULT_REVEAL_RATIO, 1] },
    );

    for (const node of nodes) observer.observe(node);
    return () => observer.disconnect();
  }, [containerRef, observeEpoch]);

  return pickStickyKey(orderedKeys, scrolledPast);
}
